import { parseAssessedNumber } from "./calgaryAssessmentOpenData";

function safeStr(value) {
  return String(value ?? "").trim();
}

/**
 * Roll identifier from saved myProperty* parcel match (no separate lookup).
 * @param {object|null|undefined} profile
 * @returns {string|null}
 */
export function deriveParcelRollIdentifier(profile) {
  const p = profile || {};
  const snap = p.myPropertyParcelSnapshot;
  const raw =
    safeStr(p.myPropertyParcelRoll) ||
    safeStr(snap?.roll_number) ||
    safeStr(snap?.roll);
  const digits = raw.replace(/\D/g, "");
  return digits || null;
}

/**
 * Saved matched parcel assessment (current-year snapshot on users/{uid}).
 * @param {object|null|undefined} profile
 * @returns {{ year: number|null, assessedValue: number }|null}
 */
export function extractCurrentAssessmentSnapshot(profile) {
  const p = profile || {};
  const snap = p.myPropertyParcelSnapshot;
  const assessedValue =
    parseAssessedNumber(p.myPropertyParcelAssessedValue) ??
    parseAssessedNumber(snap?.assessed_value ?? snap?.re_assessed_value);
  if (assessedValue == null) return null;
  const yearRaw = parseAssessedNumber(p.myPropertyParcelYear ?? snap?.roll_year);
  const year = yearRaw != null ? Math.round(yearRaw) : null;
  return { year, assessedValue };
}

/** @param {object|null|undefined} raw */
export function normalizeAssessmentHistoryRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  const year = parseAssessedNumber(raw.roll_year);
  if (year == null || year < 1900 || year > 2100) return null;
  const assessedValue = parseAssessedNumber(raw.assessed_value ?? raw.re_assessed_value);
  return {
    year: Math.round(year),
    assessedValue,
    assessmentClass: safeStr(raw.assessment_class) || null,
    source: "historical",
  };
}

/** @param {object[]} rawRows */
export function normalizeAssessmentHistoryRows(rawRows) {
  const byYear = new Map();
  for (const raw of Array.isArray(rawRows) ? rawRows : []) {
    const row = normalizeAssessmentHistoryRow(raw);
    if (!row) continue;
    const existing = byYear.get(row.year);
    if (!existing) {
      byYear.set(row.year, row);
      continue;
    }
    if (existing.assessedValue == null && row.assessedValue != null) {
      byYear.set(row.year, row);
      continue;
    }
    if (
      row.assessedValue != null &&
      existing.assessedValue != null &&
      row.assessedValue > existing.assessedValue
    ) {
      byYear.set(row.year, row);
    }
  }
  return Array.from(byYear.values());
}

/** @param {{ year: number, assessedValue: number|null }[]} rows */
export function sortAssessmentHistoryForTable(rows) {
  return [...(rows || [])].sort((a, b) => b.year - a.year);
}

/** @param {{ year: number, assessedValue: number|null }[]} rows */
export function sortAssessmentHistoryForChart(rows) {
  return [...(rows || [])].sort((a, b) => a.year - b.year);
}

/**
 * @param {number|null} current
 * @param {number|null} prior
 */
export function calculateAssessmentYearOverYearChange(current, prior) {
  if (current == null || prior == null) {
    return { dollarChange: null, percentChange: null };
  }
  const dollarChange = current - prior;
  if (!Number.isFinite(prior) || prior === 0) {
    return { dollarChange, percentChange: null };
  }
  const percentChange = (dollarChange / prior) * 100;
  return {
    dollarChange,
    percentChange: Number.isFinite(percentChange) ? percentChange : null,
  };
}

/**
 * Attach YoY vs the prior calendar year (ascending pass).
 * @param {{ year: number, assessedValue: number|null }[]} rows
 */
export function attachYearOverYearChanges(rows) {
  const asc = sortAssessmentHistoryForChart(rows);
  const valueByYear = new Map(asc.map((r) => [r.year, r.assessedValue]));
  return asc.map((row) => {
    const priorValue = valueByYear.get(row.year - 1) ?? null;
    const { dollarChange, percentChange } = calculateAssessmentYearOverYearChange(
      row.assessedValue,
      priorValue
    );
    return { ...row, dollarChange, percentChange };
  });
}

/**
 * Current assessed value — saved parcel snapshot wins over stale historical latest (CL-367B).
 * @param {object|null|undefined} profile
 * @param {{ year: number, assessedValue: number|null }[]} [historyRows]
 */
export function extractCurrentAssessedValue(profile, historyRows = []) {
  const snapshot = extractCurrentAssessmentSnapshot(profile);
  if (snapshot?.assessedValue != null) return snapshot.assessedValue;

  const sorted = sortAssessmentHistoryForTable(historyRows);
  for (const row of sorted) {
    if (row.assessedValue != null) return row.assessedValue;
  }
  return null;
}

/**
 * Chart series: all historical years plus snapshot when newer or not in roll history.
 * @param {{ year: number, assessedValue: number|null, source?: string }[]} historicalRows
 * @param {{ year: number|null, assessedValue: number }|null} snapshot
 */
export function buildAssessmentChartRows(historicalRows, snapshot) {
  const byYear = new Map(
    (historicalRows || []).map((r) => [r.year, { ...r, source: r.source || "historical" }])
  );
  if (snapshot?.assessedValue != null) {
    const year = snapshot.year;
    if (year != null) {
      const existing = byYear.get(year);
      if (!existing || existing.assessedValue !== snapshot.assessedValue) {
        byYear.set(year, {
          year,
          assessedValue: snapshot.assessedValue,
          assessmentClass: existing?.assessmentClass ?? null,
          source: "snapshot",
        });
      }
    } else {
      const maxYear = Math.max(0, ...Array.from(byYear.keys()));
      const nextYear = maxYear > 0 ? maxYear + 1 : new Date().getFullYear();
      byYear.set(nextYear, {
        year: nextYear,
        assessedValue: snapshot.assessedValue,
        assessmentClass: null,
        source: "snapshot",
      });
    }
  }
  return sortAssessmentHistoryForChart(Array.from(byYear.values()));
}

/**
 * Whether the saved snapshot should be called out separately from the historical table.
 * @param {{ year: number, assessedValue: number|null }[]} historicalRows
 * @param {{ year: number|null, assessedValue: number }|null} snapshot
 */
export function snapshotDiffersFromHistoricalLatest(historicalRows, snapshot) {
  if (snapshot?.assessedValue == null) return false;
  const asc = sortAssessmentHistoryForChart(historicalRows);
  if (!asc.length) return true;
  const maxYear = asc[asc.length - 1].year;
  if (snapshot.year != null && snapshot.year > maxYear) return true;
  if (snapshot.year != null) {
    const match = asc.find((r) => r.year === snapshot.year);
    if (match && match.assessedValue !== snapshot.assessedValue) return true;
    if (!match) return true;
  }
  const latestHist = asc[asc.length - 1];
  return latestHist.assessedValue !== snapshot.assessedValue;
}

/**
 * @param {object|null|undefined} profile
 * @param {object[]} rawRows
 */
export function buildPropertyAssessmentHistoryView(profile, rawRows) {
  const roll = deriveParcelRollIdentifier(profile);
  if (!roll) {
    return {
      hasRoll: false,
      roll: null,
      currentSnapshot: null,
      currentValue: null,
      showSnapshotBanner: false,
      tableRows: [],
      chartRows: [],
      highlightChartYear: null,
    };
  }

  const historical = normalizeAssessmentHistoryRows(rawRows).filter((r) => r.assessedValue != null);
  const currentSnapshot = extractCurrentAssessmentSnapshot(profile);
  const withYoy = attachYearOverYearChanges(historical);
  const byYear = new Map(withYoy.map((r) => [r.year, r]));
  const tableRows = sortAssessmentHistoryForTable(historical)
    .map((r) => byYear.get(r.year))
    .filter(Boolean);
  const chartRows = buildAssessmentChartRows(historical, currentSnapshot);
  const highlightChartYear =
    chartRows.length > 0 ? chartRows[chartRows.length - 1].year : currentSnapshot?.year ?? null;

  return {
    hasRoll: true,
    roll,
    currentSnapshot,
    currentValue: extractCurrentAssessedValue(profile, historical),
    showSnapshotBanner: Boolean(
      currentSnapshot?.assessedValue != null &&
        snapshotDiffersFromHistoricalLatest(historical, currentSnapshot)
    ),
    tableRows,
    chartRows,
    highlightChartYear,
  };
}
